import { devLog, devError } from '../utils/devLogger';
import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, Alert, Badge, Spinner, Row, Col } from 'react-bootstrap';
import { useAuth } from '../auth/AuthContext';
import programAssignmentService from '../services/programAssignmentService';

const FacilitatorProgramAssignment = () => {
  const { currentUser, userProfile } = useAuth();
  const [facilitators, setFacilitators] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedFacilitator, setSelectedFacilitator] = useState(null);
  const [selectedPrograms, setSelectedPrograms] = useState([]);
  const [alert, setAlert] = useState({ show: false, message: '', type: '' });

  useEffect(() => {
    if (currentUser && userProfile?.role === 'admin') {
      loadData();
    }
  }, [currentUser, userProfile]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [facilitatorsData, programsData] = await Promise.all([
        programAssignmentService.getFacilitatorsWithPrograms(),
        programAssignmentService.getAvailablePrograms()
      ]);

      setFacilitators(facilitatorsData);
      setPrograms(programsData);
    } catch (error) {
      devError('Error loading data:', error);
      showAlert('Error loading assignment data', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const showAlert = (message, type) => {
    setAlert({ show: true, message, type });
    setTimeout(() => setAlert({ show: false, message: '', type: '' }), 5000);
  };

  const handleShowModal = (facilitator) => {
    setSelectedFacilitator(facilitator);
    setSelectedPrograms(facilitator.assignedPrograms || []);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedFacilitator(null);
    setSelectedPrograms([]);
  };

  const handleProgramToggle = (programId) => {
    setSelectedPrograms(prev => {
      if (prev.includes(programId)) {
        return prev.filter(id => id !== programId);
      } else {
        return [...prev, programId];
      }
    });
  };

  const handleSaveAssignments = async () => {
    try {
      await programAssignmentService.assignFacilitatorToPrograms(
        selectedFacilitator.id,
        selectedPrograms
      );

      showAlert('Program assignments updated successfully!', 'success');
      handleCloseModal();
      loadData();
    } catch (error) {
      devError('Error saving assignments:', error);
      showAlert('Error updating assignments: ' + error.message, 'danger');
    }
  };

  const getProgramName = (programId) => {
    const program = programs.find(p => p.id === programId);
    return program ? program.name : 'Unknown Program';
  };

  const getAssignmentCount = () => {
    return facilitators.reduce((total, facilitator) => {
      return total + (facilitator.assignedPrograms?.length || 0);
    }, 0);
  };

  if (userProfile?.role !== 'admin') {
    return (
      <Alert variant="danger">
        <h5>Access Denied</h5>
        <p>Only administrators can manage program assignments.</p>
      </Alert>
    );
  }

  if (loading) {
    return (
      <div className="text-center my-4">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p className="mt-2">Loading facilitator assignments...</p>
      </div>
    );
  }

  return (
    <>
      {alert.show && (
        <Alert variant={alert.type} className="mb-3">
          {alert.message}
        </Alert>
      )}

      {/* Assignment Statistics */}
      <Row className="mb-4">
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h4 className="text-primary">{facilitators.length}</h4>
              <small className="text-muted">Total Facilitators</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h4 className="text-success">{facilitators.filter(f => f.assignedPrograms?.length > 0).length}</h4>
              <small className="text-muted">Assigned Facilitators</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h4 className="text-info">{programs.length}</h4>
              <small className="text-muted">Available Programs</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h4 className="text-warning">{getAssignmentCount()}</h4>
              <small className="text-muted">Total Assignments</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Facilitator Assignment Table */}
      <Card>
        <Card.Header>
          <h5>Facilitator Program Assignments</h5>
        </Card.Header>
        <Card.Body>
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>Facilitator</th>
                <th>Email</th>
                <th>Department</th>
                <th>Assigned Programs</th>
                <th>Assignment Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {facilitators.map((facilitator) => (
                <tr key={facilitator.id}>
                  <td>
                    <strong>{facilitator.displayName || 'Unnamed'}</strong>
                    {facilitator.specialization && (
                      <div><small className="text-muted">{facilitator.specialization}</small></div>
                    )}
                  </td>
                  <td>{facilitator.email}</td>
                  <td>{facilitator.department || 'N/A'}</td>
                  <td>
                    {facilitator.assignedPrograms?.length > 0 ? (
                      <div>
                        {facilitator.assignedProgramsDetails?.map((program, index) => (
                          <Badge
                            key={program.id}
                            bg="primary"
                            className={index > 0 ? 'ms-1' : ''}
                          >
                            {program.name}
                          </Badge>
                        ))}
                        <div className="mt-1">
                          <small className="text-muted">
                            {facilitator.assignedPrograms.length} program(s)
                          </small>
                        </div>
                      </div>
                    ) : (
                      <Badge bg="secondary">No assignments</Badge>
                    )}
                  </td>
                  <td>
                    {facilitator.programsAssignedAt ? (
                      new Date(facilitator.programsAssignedAt.seconds * 1000).toLocaleDateString()
                    ) : (
                      'Not assigned'
                    )}
                  </td>
                  <td>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => handleShowModal(facilitator)}
                    >
                      Manage Assignments
                    </Button>
                  </td>
                </tr>
              ))}
              {facilitators.length === 0 && (
                <tr>
                  <td colSpan="6" className="text-center text-muted py-4">
                    No facilitators found. Create facilitator accounts to assign programs.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* Assignment Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            Manage Program Assignments - {selectedFacilitator?.displayName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedFacilitator && (
            <>
              <div className="mb-3">
                <p><strong>Facilitator:</strong> {selectedFacilitator.displayName}</p>
                <p><strong>Email:</strong> {selectedFacilitator.email}</p>
                <p><strong>Department:</strong> {selectedFacilitator.department || 'N/A'}</p>
              </div>

              <h6>Available Programs:</h6>
              <div className="border rounded p-3" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                {programs.length === 0 ? (
                  <p className="text-muted">No programs available for assignment.</p>
                ) : (
                  programs.map((program) => (
                    <Form.Check
                      key={program.id}
                      type="checkbox"
                      id={`program-${program.id}`}
                      label={
                        <div>
                          <strong>{program.name}</strong>
                          {program.description && (
                            <div><small className="text-muted">{program.description}</small></div>
                          )}
                          <div>
                            <Badge bg="info" className="me-1">{program.category}</Badge>
                            <Badge bg="secondary">{program.level}</Badge>
                          </div>
                        </div>
                      }
                      checked={selectedPrograms.includes(program.id)}
                      onChange={() => handleProgramToggle(program.id)}
                      className="mb-3"
                    />
                  ))
                )}
              </div>

              {selectedPrograms.length > 0 && (
                <div className="mt-3">
                  <h6>Selected Programs ({selectedPrograms.length}):</h6>
                  <div>
                    {selectedPrograms.map(programId => (
                      <Badge key={programId} bg="primary" className="me-1 mb-1">
                        {getProgramName(programId)}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveAssignments}>
            Save Assignments
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default FacilitatorProgramAssignment;